import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { notifications } from "@mantine/notifications"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useState } from "react"
import { withViewTransition } from "../../hooks/useViewTransition"
import { updateUserThemePreference } from "../../lib/api"
import { queryKeys } from "../../lib/query-keys"
import { applyTheme, persistThemeCookie, type TVoxTheme } from "../../lib/theme-mode"
import type { IProps } from "./interfaces"
import { VoxcanonThemeToggleView } from "./VoxcanonThemeToggleView"

function readDocumentTheme(): TVoxTheme {
  if (typeof document === "undefined") return "fantasy"
  const attr = document.documentElement.dataset.theme
  return attr === "scifi" ? "scifi" : "fantasy"
}

export const VoxcanonThemeToggle = ({ initialTheme, onChange }: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  // The `<html data-theme>` attribute is the source of truth — start
  // from it (or the supplied initial), then keep in sync via a
  // MutationObserver so external changes (e.g. the post-auth sync
  // effect) flow back into the pill without re-controlling via prop.
  const [current, setCurrent] = useState<TVoxTheme>(initialTheme ?? readDocumentTheme())
  const queryClient = useQueryClient()

  useEffect(() => {
    if (typeof document === "undefined") return
    const root = document.documentElement
    const sync = (): void => {
      const next = readDocumentTheme()
      setCurrent((prev) => (prev === next ? prev : next))
    }
    sync()
    const observer = new MutationObserver(sync)
    observer.observe(root, { attributes: true, attributeFilter: ["data-theme"] })
    return () => observer.disconnect()
  }, [])

  const mutation = useMutation({
    mutationFn: (theme: TVoxTheme) => updateUserThemePreference(theme),
    onError: () => {
      notifications.show({
        color: "danger",
        title: i18n._(msg`Could not save theme`),
        message: i18n._(msg`Your choice will only apply on this browser until we can reach the server.`),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userThemePreference() })
    },
  })

  const handleChange = useCallback(
    (next: TVoxTheme): void => {
      withViewTransition(() => {
        applyTheme(next)
      })
      persistThemeCookie(next)
      setCurrent(next)
      onChange?.(next)
      mutation.mutate(next)
    },
    [mutation, onChange],
  )

  return <VoxcanonThemeToggleView currentTheme={current} onChange={handleChange} />
}
